import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  Line,
} from "recharts";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const MetricsTab = ({ project }) => {
  const [activeSubTab, setActiveSubTab] = useState("finances");
  const [metricsData, setMetricsData] = useState({
    revenueChartData: null,
    timesheetChartData: null,
    billsChartData: null,
    pieceworkerChartData: null,
    contractChartData: null,
    estimatedCostChartData: null,
    tableData: [],
  });
  const [financeData, setFinanceData] = useState({
    originalContractValue: 0,
    changeOrderValue: 0,
    totalEstimatedCost: 0,
    originalEstimatedCost: 0,
    changeOrderImpact: 0,
    tAndMCost: 0,
    varianceImpact: 0,
    purchaseOrders: [],
    workOrders: [],
    totalWsibCost: 0,
    totalBurdenCost: 0,
    totalPieceworkerBurdenCost: 0,
    totalWorkOrderActualSubtotal: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Colors for charts
  const COLORS = [
    "#4CAF50", // Green
    "#2196F3", // Blue
    "#F44336", // Red
    "#FF9800", // Orange
    "#3F51B5", // Indigo
    "#9C27B0", // Purple
    "#FF5722", // Deep Orange
  ];

  // Custom Tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-700 p-3 border border-gray-600 rounded shadow-md">
          <p className="text-sm font-semibold text-gray-200">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs text-gray-300">
              {entry.name}:{" "}
              {entry.dataKey.includes("Cost") ||
              entry.dataKey.includes("Invoiced") ||
              entry.dataKey.includes("Orders") ||
              entry.dataKey.includes("Holdback")
                ? `$${entry.value.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}`
                : `$${entry.value.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Function to calculate cost breakdown
  const calculateBreakdown = () => {
    const originalEstimatedCostBeforeVariance = (project.budgets || [])
      .filter(
        (budget) =>
          !budget.isVariance &&
          !budget.isChangeOrder &&
          !budget.isTimeAndMaterial,
      )
      .flatMap((budget) => budget.lineItems || [])
      .reduce((sum, item) => sum + (item.amount || 0), 0);

    const tAndMCost = (project.budgets || [])
      .filter((budget) => budget.isTimeAndMaterial && !budget.isChangeOrder)
      .flatMap((budget) => budget.lineItems || [])
      .reduce((sum, item) => sum + (item.amount || 0), 0);

    const varianceImpact = (project.budgets || [])
      .filter(
        (budget) =>
          budget.isVariance &&
          (project.budgets || []).find((b) => b._id === budget.originalBudgetId)
            ?.isChangeOrder !== true,
      )
      .flatMap((budget) => budget.lineItems || [])
      .reduce((sum, item) => sum + (item.costDiff || 0), 0);

    const changeOrderImpact = (project.budgets || [])
      .filter((budget) => !budget.isVariance && budget.isChangeOrder)
      .reduce((sum, budget) => {
        const budgetAmount = (budget.lineItems || []).reduce(
          (acc, item) => acc + (item.amount || 0),
          0,
        );
        const varianceCostDiff = (project.budgets || [])
          .filter((vb) => vb.isVariance && vb.originalBudgetId === budget._id)
          .flatMap((vb) => vb.lineItems || [])
          .reduce((acc, item) => acc + (item.costDiff || 0), 0);
        return sum + budgetAmount + varianceCostDiff;
      }, 0);

    const originalEstimatedCost =
      originalEstimatedCostBeforeVariance - varianceImpact;
    const totalEstimatedCost =
      originalEstimatedCostBeforeVariance + tAndMCost + changeOrderImpact;

    return {
      totalEstimatedCost,
      originalEstimatedCost,
      changeOrderImpact,
      tAndMCost,
      varianceImpact,
    };
  };

  useEffect(() => {
    const fetchMetricsData = async () => {
      try {
        const token = localStorage.getItem("token");
        const projectId = project._id;
        const currentYear = new Date().getFullYear();

        const [
          sovResponse,
          timesheetResponse,
          pieceworkerResponse,
          billsResponse,
          materialOrdersResponse,
          companyProfileResponse,
        ] = await Promise.all([
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/sovs/project/${projectId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/timesheets/project/${projectId}/all`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs?project=${projectId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/bills/project/${projectId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/project/${projectId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/company-profile`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ]);

        const companyProfile = companyProfileResponse.data;

        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const totalInvoiced = new Array(12).fill(0);
        const totalCosts = new Array(12).fill(0);
        const timesheetApproved = new Array(12).fill(0);
        const timesheetPending = new Array(12).fill(0);
        const billsPaid = new Array(12).fill(0);
        const billsUnpaid = new Array(12).fill(0);
        const pieceworkerApproved = new Array(12).fill(0);
        const pieceworkerPending = new Array(12).fill(0);

        let totalInvoicedSum = 0;
        let totalCompletedToDateSum = 0;
        let withHoldback = false;

        (sovResponse.data.scheduleOfValues || []).forEach((sov) => {
          const date = new Date(sov.createdAt);
          totalInvoicedSum += parseFloat(sov.totalInvoiced || 0);
          sov.items.forEach((item) => {
            totalCompletedToDateSum += parseFloat(
              item.totalCompletedToDate || 0,
            );
          });
          if (sov.withHoldback) {
            withHoldback = true;
          }
          if (date.getFullYear() === currentYear) {
            totalInvoiced[date.getMonth()] += parseFloat(
              sov.totalInvoiced || 0,
            );
          }
        });

        const companyTaxRate = parseFloat(
          companyProfile?.defaultTax?.taxRate || 0,
        );
        const totalProjectHoldback = withHoldback
          ? (
              totalCompletedToDateSum *
              (1 + companyTaxRate / 100) *
              0.1
            ).toFixed(2)
          : "0.00";

        (timesheetResponse.data.timesheets || []).forEach((ts) => {
          const date = new Date(ts.start);
          if (date.getFullYear() === currentYear && ts.totalCost) {
            const monthIdx = date.getMonth();
            totalCosts[monthIdx] += parseFloat(ts.totalCost || 0);
            if (ts.status === true && ts.Rejected === false) {
              timesheetApproved[monthIdx] += parseFloat(ts.totalCost || 0);
            } else if (!ts.status || ts.clockInStatus || !ts.end) {
              timesheetPending[monthIdx] += parseFloat(ts.totalCost || 0);
            }
          }
        });

        (pieceworkerResponse.data || []).forEach((pw) => {
          const date = new Date(pw.submittedAt || pw.workCompletedDate);
          if (date.getFullYear() === currentYear && pw.totalCost) {
            const monthIdx = date.getMonth();
            totalCosts[monthIdx] += parseFloat(pw.totalCost || 0);
            if (pw.status === "approved") {
              pieceworkerApproved[monthIdx] += parseFloat(pw.totalCost || 0);
            } else if (pw.status === "pending") {
              pieceworkerPending[monthIdx] += parseFloat(pw.totalCost || 0);
            }
          }
        });

        (billsResponse.data || []).forEach((bill) => {
          const date = new Date(bill.invoiceDate);
          if (date.getFullYear() === currentYear && bill.subtotal) {
            const monthIdx = date.getMonth();
            totalCosts[monthIdx] += parseFloat(bill.subtotal || 0);
            if (
              bill.processingStatus === "processed" &&
              (bill.invoiceStatus === "paid" || bill.datePaid)
            ) {
              billsPaid[monthIdx] += parseFloat(bill.subtotal || 0);
            } else {
              billsUnpaid[monthIdx] += parseFloat(bill.subtotal || 0);
            }
          }
        });

        const gpm = totalInvoiced.map((invoiced, idx) => {
          const costs = totalCosts[idx];
          return invoiced > 0 ? ((invoiced - costs) / invoiced) * 100 : 0;
        });

        const revenueChartData = months.map((month, idx) => ({
          month,
          "Total Invoiced": totalInvoiced[idx],
          "Gross Profit Margin": gpm[idx],
          "GPM Goal": 30,
        }));

        const timesheetChartData = months.map((month, idx) => ({
          month,
          Approved: timesheetApproved[idx],
          Pending: timesheetPending[idx],
        }));

        const billsChartData = months.map((month, idx) => ({
          month,
          Paid: billsPaid[idx],
          Unpaid: billsUnpaid[idx],
        }));

        const pieceworkerChartData = months.map((month, idx) => ({
          month,
          Approved: pieceworkerApproved[idx],
          Pending: pieceworkerPending[idx],
        }));

        const totalInvoicedTotal = totalInvoicedSum;
        const originalContractValue = project.originalContractValue || 0;
        const changeOrderValue = project.changeOrderContractValue || 0;

        const contractChartData = [
          {
            name: "Total Invoiced",
            "Total Invoiced": totalInvoicedTotal,
            "Total Holdback": parseFloat(totalProjectHoldback),
          },
          {
            name: "Contract Value",
            "Original Contract": originalContractValue,
            "Change Orders": changeOrderValue,
          },
        ];

        const {
          totalEstimatedCost,
          originalEstimatedCost,
          changeOrderImpact,
          tAndMCost,
          varianceImpact,
        } = calculateBreakdown();

        const bills = billsResponse.data || [];
        const timesheets = timesheetResponse.data.timesheets || [];
        const pieceworkerLogs = pieceworkerResponse.data || [];
        const materialOrders = materialOrdersResponse.data || [];

        const purchaseOrders = (project.purchaseOrders || []).map((po) => {
          const estimatedCost =
            po.lineItems?.reduce(
              (sum, item) => sum + (item.unitCost || 0) * (item.quantity || 0),
              0,
            ) ||
            po.estimatedCost ||
            0;

          const poMaterialOrders = materialOrders.filter(
            (mo) =>
              (mo.purchaseOrder?._id?.toString() === po._id.toString() ||
                mo.purchaseOrder?.toString() === po._id.toString()) &&
              ["confirmed", "delivered"].includes(mo.status),
          );

          const pendingMOCost = poMaterialOrders.reduce((sum, mo) => {
            const itemsSubtotal = (mo.items || []).reduce(
              (sum, item) => sum + (item.quantity || 0) * (item.price || 0),
              0,
            );
            const surchargesTotal = (mo.surcharges || []).reduce(
              (sum, surcharge) => sum + (surcharge.price || 0),
              0,
            );
            const moTotal = itemsSubtotal + surchargesTotal;

            const moBills = bills.filter(
              (bill) =>
                bill.relatedMO &&
                (bill.relatedMO._id?.toString() === mo._id.toString() ||
                  bill.relatedMO?.toString() === mo._id.toString()) &&
                bill.processingStatus === "processed",
            );
            const processedBillTotal = moBills.reduce((sum, bill) => {
              const billSubtotal = bill.subtotal
                ? parseFloat(bill.subtotal || 0)
                : parseFloat(bill.total || 0) /
                  (1 + (companyProfile?.defaultTax?.taxRate || 0) / 100);
              return sum + billSubtotal;
            }, 0);

            return sum + Math.max(0, moTotal - processedBillTotal);
          }, 0);

          const poBills = bills.filter(
            (bill) =>
              bill.relatedPO &&
              bill.relatedPO._id.toString() === po._id.toString() &&
              !isNaN(parseFloat(bill.total)),
          );

          const actualCost = poBills
            .filter((bill) => bill.processingStatus === "processed")
            .reduce((sum, bill) => {
              const billSubtotal = bill.subtotal
                ? parseFloat(bill.subtotal || 0)
                : parseFloat(bill.total || 0) /
                  (1 + (companyProfile?.defaultTax?.taxRate || 0) / 100);
              return sum + billSubtotal;
            }, 0);

          const pendingBillCost = poBills
            .filter(
              (bill) => bill.processingStatus === "pending" && !bill.relatedMO,
            )
            .reduce((sum, bill) => {
              const billSubtotal = bill.subtotal
                ? parseFloat(bill.subtotal || 0)
                : parseFloat(bill.total || 0) /
                  (1 + (companyProfile?.defaultTax?.taxRate || 0) / 100);
              return sum + billSubtotal;
            }, 0);

          const totalPendingCost = pendingMOCost + pendingBillCost;
          const totalForecasted = actualCost + totalPendingCost;
          const remainingCost = estimatedCost - actualCost - pendingMOCost;

          return {
            poId: po._id,
            title: po.title,
            poNumber: po.poNumber || "N/A",
            estimatedCost: estimatedCost || 0,
            actualCost: actualCost || 0,
            pendingMOCost: pendingMOCost || 0,
            pendingBillCost: pendingBillCost || 0,
            totalForecasted: totalForecasted || 0,
            remainingCost: remainingCost || 0,
          };
        });

        const workOrders = (project.workOrders || []).map((wo) => {
          const estimatedCost =
            wo.lineItems?.reduce(
              (sum, item) =>
                sum + (item.unitCost || 0) * (item.qtyEstimated || 0),
              0,
            ) ||
            wo.estimatedCost ||
            0;

          const woBills = bills.filter(
            (bill) =>
              bill.relatedWO &&
              bill.relatedWO._id.toString() === wo._id.toString() &&
              !isNaN(parseFloat(bill.total)),
          );

          const billsActualCost = woBills
            .filter((bill) => bill.processingStatus === "processed")
            .reduce((sum, bill) => {
              const billSubtotal = bill.subtotal
                ? parseFloat(bill.subtotal || 0)
                : parseFloat(bill.total || 0) /
                  (1 + (companyProfile?.defaultTax?.taxRate || 0) / 100);
              return sum + billSubtotal;
            }, 0);

          const billsPendingCost = woBills
            .filter((bill) => bill.processingStatus === "pending")
            .reduce((sum, bill) => {
              const billSubtotal = bill.subtotal
                ? parseFloat(bill.subtotal || 0)
                : parseFloat(bill.total || 0) /
                  (1 + (companyProfile?.defaultTax?.taxRate || 0) / 100);
              return sum + billSubtotal;
            }, 0);

          const woPieceworkerLogs = pieceworkerLogs.filter(
            (pw) =>
              pw.workOrder &&
              (pw.workOrder._id?.toString() === wo._id.toString() ||
                pw.workOrder.toString() === wo._id.toString()) &&
              !isNaN(parseFloat(pw.totalCost)),
          );

          const pieceworkerActualCostBreakdown = woPieceworkerLogs
            .filter((pw) => pw.status === "approved")
            .reduce(
              (acc, pw) => {
                const lineItems = pw.lineItems || [];
                const subtotal = lineItems.reduce(
                  (sum, item) => sum + parseFloat(item.subtotal || 0),
                  0,
                );
                const wsibCost = lineItems.reduce(
                  (sum, item) => sum + parseFloat(item.wsibCost || 0),
                  0,
                );
                const pieceworkerBurdenCost = lineItems.reduce(
                  (sum, item) =>
                    sum + parseFloat(item.pieceworkerBurdenCost || 0),
                  0,
                );
                return {
                  subtotal: acc.subtotal + subtotal,
                  wsibCost: acc.wsibCost + wsibCost,
                  pieceworkerBurdenCost:
                    acc.pieceworkerBurdenCost + pieceworkerBurdenCost,
                  total: acc.total + parseFloat(pw.totalCost || 0),
                };
              },
              { subtotal: 0, wsibCost: 0, pieceworkerBurdenCost: 0, total: 0 },
            );

          const pieceworkerPendingCostBreakdown = woPieceworkerLogs
            .filter((pw) => pw.status === "pending")
            .reduce(
              (acc, pw) => {
                const lineItems = pw.lineItems || [];
                const subtotal = lineItems.reduce(
                  (sum, item) => sum + parseFloat(item.subtotal || 0),
                  0,
                );
                const wsibCost = lineItems.reduce(
                  (sum, item) => sum + parseFloat(item.wsibCost || 0),
                  0,
                );
                const pieceworkerBurdenCost = lineItems.reduce(
                  (sum, item) =>
                    sum + parseFloat(item.pieceworkerBurdenCost || 0),
                  0,
                );
                return {
                  subtotal: acc.subtotal + subtotal,
                  wsibCost: acc.wsibCost + wsibCost,
                  pieceworkerBurdenCost:
                    acc.pieceworkerBurdenCost + pieceworkerBurdenCost,
                  total: acc.total + parseFloat(pw.totalCost || 0),
                };
              },
              { subtotal: 0, wsibCost: 0, pieceworkerBurdenCost: 0, total: 0 },
            );

          const woTimesheets = timesheets.filter(
            (ts) =>
              ts.workOrderId?.toString() === wo._id.toString() &&
              !isNaN(parseFloat(ts.totalCost)),
          );

          let actualCostBreakdown,
            pendingCostBreakdown,
            actualCost,
            pendingCost;

          if (wo.workType === "hourly") {
            const timesheetActualCostBreakdown = woTimesheets
              .filter((ts) => ts.status === true && ts.Rejected === false)
              .reduce(
                (acc, ts) => ({
                  subtotal: acc.subtotal + parseFloat(ts.subtotal || 0),
                  wsibCost: acc.wsibCost + parseFloat(ts.wsibCost || 0),
                  burdenCost: acc.burdenCost + parseFloat(ts.burdenCost || 0),
                  total: acc.total + parseFloat(ts.totalCost || 0),
                }),
                { subtotal: 0, wsibCost: 0, burdenCost: 0, total: 0 },
              );

            const timesheetPendingCostBreakdown = woTimesheets
              .filter(
                (ts) =>
                  (!ts.status && ts.Rejected === false) ||
                  ts.clockInStatus ||
                  !ts.end,
              )
              .reduce(
                (acc, ts) => ({
                  subtotal: acc.subtotal + parseFloat(ts.subtotal || 0),
                  wsibCost: acc.wsibCost + parseFloat(ts.wsibCost || 0),
                  burdenCost: acc.burdenCost + parseFloat(ts.burdenCost || 0),
                  total: acc.total + parseFloat(ts.totalCost || 0),
                }),
                { subtotal: 0, wsibCost: 0, burdenCost: 0, total: 0 },
              );

            actualCostBreakdown = {
              subtotal: timesheetActualCostBreakdown.subtotal,
              wsibCost: timesheetActualCostBreakdown.wsibCost,
              burdenCost: timesheetActualCostBreakdown.burdenCost,
              pieceworkerBurdenCost: 0,
              total: timesheetActualCostBreakdown.total,
            };

            pendingCostBreakdown = {
              subtotal: timesheetPendingCostBreakdown.subtotal,
              wsibCost: timesheetPendingCostBreakdown.wsibCost,
              burdenCost: timesheetPendingCostBreakdown.burdenCost,
              pieceworkerBurdenCost: 0,
              total: timesheetPendingCostBreakdown.total,
            };

            actualCost = actualCostBreakdown.total;
            pendingCost = pendingCostBreakdown.total;
          } else {
            const timesheetActualCostBreakdown = woTimesheets
              .filter((ts) => ts.status === true && ts.Rejected === false)
              .reduce(
                (acc, ts) => ({
                  subtotal: acc.subtotal + parseFloat(ts.subtotal || 0),
                  wsibCost: acc.wsibCost + parseFloat(ts.wsibCost || 0),
                  burdenCost: acc.burdenCost + parseFloat(ts.burdenCost || 0),
                  total: acc.total + parseFloat(ts.totalCost || 0),
                }),
                { subtotal: 0, wsibCost: 0, burdenCost: 0, total: 0 },
              );

            const timesheetPendingCostBreakdown = woTimesheets
              .filter(
                (ts) =>
                  (!ts.status &&
                    ts.Rejected === false &&
                    ts.taskTypeName === "hourly") ||
                  ts.clockInStatus ||
                  !ts.end,
              )
              .reduce(
                (acc, ts) => ({
                  subtotal: acc.subtotal + parseFloat(ts.subtotal || 0),
                  wsibCost: acc.wsibCost + parseFloat(ts.wsibCost || 0),
                  burdenCost: acc.burdenCost + parseFloat(ts.burdenCost || 0),
                  total: acc.total + parseFloat(ts.totalCost || 0),
                }),
                { subtotal: 0, wsibCost: 0, burdenCost: 0, total: 0 },
              );

            actualCostBreakdown = {
              subtotal:
                billsActualCost +
                timesheetActualCostBreakdown.subtotal +
                pieceworkerActualCostBreakdown.subtotal,
              wsibCost:
                timesheetActualCostBreakdown.wsibCost +
                pieceworkerActualCostBreakdown.wsibCost,
              burdenCost: timesheetActualCostBreakdown.burdenCost,
              pieceworkerBurdenCost:
                pieceworkerActualCostBreakdown.pieceworkerBurdenCost,
              total:
                billsActualCost +
                timesheetActualCostBreakdown.total +
                pieceworkerActualCostBreakdown.total,
            };

            pendingCostBreakdown = {
              subtotal:
                billsPendingCost +
                timesheetPendingCostBreakdown.subtotal +
                pieceworkerPendingCostBreakdown.subtotal,
              wsibCost:
                timesheetPendingCostBreakdown.wsibCost +
                pieceworkerPendingCostBreakdown.wsibCost,
              burdenCost: timesheetPendingCostBreakdown.burdenCost,
              pieceworkerBurdenCost:
                pieceworkerPendingCostBreakdown.pieceworkerBurdenCost,
              total:
                billsPendingCost +
                timesheetPendingCostBreakdown.total +
                pieceworkerPendingCostBreakdown.total,
            };

            actualCost = actualCostBreakdown.total;
            pendingCost = pendingCostBreakdown.total;
          }

          const remainingCost = estimatedCost - actualCost - pendingCost;

          return {
            woId: wo._id,
            title: wo.title,
            woNumber: wo.woNumber || "N/A",
            estimatedCost: estimatedCost || 0,
            actualCost: actualCost || 0,
            actualCostBreakdown,
            pendingCost: pendingCost || 0,
            pendingCostBreakdown,
            remainingCost: remainingCost || 0,
          };
        });

        const totalActualCost =
          purchaseOrders.reduce((sum, po) => sum + (po.actualCost || 0), 0) +
          workOrders.reduce((sum, wo) => sum + (wo.actualCost || 0), 0);

        const totalWorkOrderActualSubtotal = workOrders.reduce(
          (sum, wo) => sum + (wo.actualCostBreakdown.subtotal || 0),
          0,
        );

        const totalWorkOrderActualCost = workOrders.reduce(
          (sum, wo) => sum + (wo.actualCost || 0),
          0,
        );
        const totalPurchaseOrderActualCost = purchaseOrders.reduce(
          (sum, po) => sum + (po.actualCost || 0),
          0,
        );
        const totalWsibCost = workOrders.reduce(
          (sum, wo) => sum + (wo.actualCostBreakdown.wsibCost || 0),
          0,
        );
        const totalBurdenCost = workOrders.reduce(
          (sum, wo) => sum + (wo.actualCostBreakdown.burdenCost || 0),
          0,
        );
        const totalPieceworkerBurdenCost = workOrders.reduce(
          (sum, wo) =>
            sum + (wo.actualCostBreakdown.pieceworkerBurdenCost || 0),
          0,
        );
        const totalForecastedCost =
          purchaseOrders.reduce(
            (sum, po) =>
              sum +
              (po.actualCost || 0) +
              (po.pendingMOCost || 0) +
              (po.pendingBillCost || 0),
            0,
          ) +
          workOrders.reduce(
            (sum, wo) => sum + (wo.actualCost || 0) + (wo.pendingCost || 0),
            0,
          );

        const estimatedCostChartData = [
          {
            name: "Estimated Cost",
            "Original Estimated Cost": originalEstimatedCost,
            "T&M Cost": tAndMCost,
            "Change Order Impact": changeOrderImpact,
            "Variance Impact": varianceImpact,
          },
          {
            name: "Actual Cost",
            "Work Order Actual Cost": totalWorkOrderActualCost,
            "Purchase Order Actual Cost": totalPurchaseOrderActualCost,
            "Pending Costs": totalForecastedCost - totalActualCost,
          },
        ];

        const tableData = months.map((month, idx) => ({
          month,
          totalInvoiced: totalInvoiced[idx].toFixed(2),
          totalCosts: totalCosts[idx].toFixed(2),
          gpm: gpm[idx].toFixed(2),
        }));

        // Calculate Target Revenue (assumed as originalContractValue distributed evenly across 12 months)
        const targetRevenue = (project.originalContractValue || 0) / 12;

        setMetricsData({
          revenueChartData,
          timesheetChartData,
          billsChartData,
          pieceworkerChartData,
          contractChartData,
          estimatedCostChartData,
          tableData,
        });

        setFinanceData({
          originalContractValue: project.originalContractValue || 0,
          changeOrderValue: project.changeOrderContractValue || 0,
          totalEstimatedCost,
          originalEstimatedCost,
          changeOrderImpact,
          tAndMCost,
          varianceImpact,
          purchaseOrders,
          workOrders,
          totalWsibCost,
          totalBurdenCost,
          totalPieceworkerBurdenCost,
          totalWorkOrderActualSubtotal,
        });

        setLoading(false);
      } catch (err) {
        console.error("Error fetching metrics:", err);
        setError(
          "Failed to load metrics data: " +
            (err.response?.data?.message || err.message),
        );
        setLoading(false);
      }
    };

    fetchMetricsData();
  }, [project]);

  const currentYear = new Date().getFullYear();

  const formatNumber = (num) =>
    (num ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  if (loading)
    return (
      <div className="text-center py-4 text-gray-200">Loading metrics...</div>
    );
  if (error)
    return <div className="text-red-400 text-center py-4">{error}</div>;

  return (
    <div className="bg-gray-200 rounded-lg ">
      <div className="mb-4">
        <nav className="flex space-x-4" aria-label="Tabs">
          <button
            onClick={() => setActiveSubTab("finances")}
            className={classNames(
              activeSubTab === "finances"
                ? "bg-orange-500 text-white font-semibold"
                : "text-gray-500 hover:text-gray-700",
              "rounded-md px-3 py-2 text-sm font-medium",
            )}
          >
            Finances
          </button>
          <button
            onClick={() => setActiveSubTab("Metrics")}
            className={classNames(
              activeSubTab === "Metrics"
                ? "bg-orange-500 text-white font-semibold"
                : "text-gray-500 hover:text-gray-700",
              "rounded-md px-3 py-2 text-sm font-medium",
            )}
          >
            Metrics
          </button>
        </nav>
      </div>

      {activeSubTab === "Metrics" && (
        <div className="grid grid-cols-1 gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200 mb-4">
                Revenue and GPM ({currentYear})
              </h3>
              {console.log(metricsData.revenueChartData)}
              {metricsData.revenueChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart
                    data={metricsData.revenueChartData.map((item) => ({
                      ...item,
                    }))}
                    margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                  >
                    <XAxis
                      dataKey="month"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      yAxisId="left"
                      label={{
                        value: "Revenue ($)",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#e5e7eb",
                        fontSize: 12,
                        dx: -10, // Moves label left
                      }}
                      padding={{ top: 20 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <YAxis
                      yAxisId="right"
                      padding={{ top: 20 }}
                      orientation="right"
                      label={{
                        value: "GPM (%)",
                        angle: -90,
                        position: "insideRight",
                        fill: "#e5e7eb",
                        fontSize: 12,
                      }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                      domain={[0, 100]} // Focus on 0% to 30% range
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="Total Invoiced"
                      name="Total Invoiced ($)"
                      fill={COLORS[1] || "#82ca9d"} // Greenish fill for bars
                      radius={[12, 12, 0, 0]} // Rounded top corners
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="Gross Profit Margin"
                      name="Gross Profit Margin (%)"
                      stroke={COLORS[2] || "#ffc658"} // Yellow for visibility
                      strokeWidth={2}
                      dot={{ r: 4, stroke: COLORS[2] || "#ffc658" }} // Visible dots
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="GPM Goal"
                      name="GPM Goal (30%)"
                      stroke={COLORS[0] || "#FF0000"} // Red for visibility
                      strokeDasharray="5 5" // Dashed line
                      strokeWidth={3}
                      dot={{ r: 4, stroke: COLORS[0] || "#FF0000" }} // Visible dots
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No revenue data available
                </p>
              )}
            </div>
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200 mb-4">
                Timesheet Costs ({currentYear})
              </h3>
              {metricsData.timesheetChartData &&
              metricsData.timesheetChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={metricsData.timesheetChartData}
                    margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                  >
                    <XAxis
                      dataKey="month"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      label={{
                        value: "Costs ($)",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#e5e7eb",

                        fontSize: 12,
                      }}
                      padding={{ top: 20 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      dataKey="Approved"
                      name="Approved"
                      fill={COLORS[0]}
                      stackId="timesheet"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Pending"
                      name="Pending"
                      fill={COLORS[3]}
                      stackId="timesheet"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No timesheet data available
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200 mb-4">
                Bills Costs ({currentYear})
              </h3>
              {metricsData.billsChartData &&
              metricsData.billsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={metricsData.billsChartData}
                    margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                  >
                    <XAxis
                      dataKey="month"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      label={{
                        value: "Costs ($)",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#e5e7eb",
                        fontSize: 12,
                      }}
                      padding={{ top: 20 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      dataKey="Paid"
                      name="Paid"
                      fill={COLORS[0]}
                      stackId="bills"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Unpaid"
                      name="Unpaid"
                      fill={COLORS[3]}
                      stackId="bills"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No bills data available
                </p>
              )}
            </div>
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200 mb-4">
                Pieceworker Costs ({currentYear})
              </h3>
              {metricsData.pieceworkerChartData &&
              metricsData.pieceworkerChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={metricsData.pieceworkerChartData}
                    margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                  >
                    <XAxis
                      dataKey="month"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      label={{
                        value: "Costs ($)",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#e5e7eb",
                        fontSize: 12,
                      }}
                      padding={{ top: 20 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      dataKey="Approved"
                      name="Approved"
                      fill={COLORS[0]}
                      stackId="pieceworker"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Pending"
                      name="Pending"
                      fill={COLORS[3]}
                      stackId="pieceworker"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No pieceworker data available
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-12">
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200">
                Total Invoiced vs. Contract
              </h3>
              {metricsData.contractChartData &&
              metricsData.contractChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart
                    data={metricsData.contractChartData}
                    margin={{
                      top: 50,
                      right: 40,
                      bottom: 150,
                      left: 40,
                    }}
                  >
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      label={{
                        value: "Amount ($)",
                        angle: -90,
                        offset: -15,
                        position: "insideLeft",
                        fill: "#e5e7eb",
                        fontSize: 16,
                      }}
                      padding={{ top: 40 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      dataKey="Total Invoiced"
                      name="Total Invoiced"
                      fill={COLORS[1]}
                      stackId="invoiced"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Total Holdback"
                      name="Total Holdback ($)"
                      fill={COLORS[3]}
                      stackId="invoiced"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Original Contract"
                      name="Original Contract"
                      fill={COLORS[0]}
                      stackId="contract"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Change Orders"
                      name="Change Orders"
                      fill={COLORS[2]}
                      stackId="contract"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No contract data available
                </p>
              )}
            </div>
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200 mb-4">
                Estimated vs. Actual Cost Breakdown
              </h3>
              {metricsData.estimatedCostChartData &&
              metricsData.estimatedCostChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart
                    data={metricsData.estimatedCostChartData}
                    margin={{ top: 20, right: 20, bottom: 175, left: 20 }}
                  >
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      label={{
                        value: "Amount",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#e5e7eb",
                        offset: -5,
                        fontSize: 16,
                      }}
                      padding={{ top: 40 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      dataKey="Original Estimated Cost"
                      name="Original Estimated Cost"
                      fill={COLORS[0]}
                      stackId="estimated"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="T&M Cost"
                      name="T&M Cost"
                      fill={COLORS[1]}
                      stackId="estimated"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Change Order Impact"
                      name="Change Order Impact"
                      fill={COLORS[3]}
                      stackId="estimated"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Variance Impact"
                      name="Variance Impact"
                      fill={COLORS[5]}
                      stackId="estimated"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Work Order Actual Cost"
                      name="Work Order Actual Cost"
                      fill={COLORS[4]}
                      stackId="actual"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Purchase Order Actual Cost"
                      name="Purchase Order Actual Cost"
                      fill={COLORS[2]}
                      stackId="actual"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Pending Costs"
                      name="Pending Costs"
                      fill={COLORS[6]}
                      stackId="actual"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No cost data available
                </p>
              )}
            </div>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg shadow overflow-auto">
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Monthly Summary
            </h3>
            <table className="min-w-full divide-y divide-gray-600">
              <thead className="bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Invoiced ($)
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Costs ($)
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    GPM (%)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {metricsData.tableData.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                      {row.month}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                      {Number(row.totalInvoiced).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                      {Number(row.totalCosts).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                      {row.gpm}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSubTab === "finances" && (
        <div>
          <div className="bg-gray-700 p-4 rounded-lg shadow mb-6">
            <h3 className="text-md font-semibold text-gray-200 mb-2">
              Contract Summary
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-400">Current Contract Value</p>
                <p className="text-lg font-bold text-gray-200">
                  $
                  {formatNumber(
                    financeData.originalContractValue +
                      financeData.changeOrderValue,
                  )}
                </p>
                <div className="text-sm text-gray-400 mt-1">
                  <p>
                    Original Contract Value: $
                    {formatNumber(financeData.originalContractValue)}
                  </p>
                  <p>
                    Change Order Value: $
                    {formatNumber(financeData.changeOrderValue)}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Estimated Cost</p>
                <p className="text-lg font-medium text-gray-200">
                  ${formatNumber(financeData.totalEstimatedCost)}
                </p>
                <div className="text-sm text-gray-400 mt-1">
                  <p>
                    Original Estimated Cost: $
                    {formatNumber(financeData.originalEstimatedCost)}
                  </p>
                  <p>T&M Cost: ${formatNumber(financeData.tAndMCost)}</p>
                  <p>
                    Change Order Impact: $
                    {formatNumber(financeData.changeOrderImpact)}
                  </p>
                  <p>
                    Variance Impact: ${formatNumber(financeData.varianceImpact)}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Actual Cost</p>
                <p className="text-lg font-medium text-gray-200">
                  $
                  {formatNumber(
                    financeData.purchaseOrders.reduce(
                      (sum, po) => sum + (po.actualCost || 0),
                      0,
                    ) +
                      financeData.workOrders.reduce(
                        (sum, wo) => sum + (wo.actualCost || 0),
                        0,
                      ),
                  )}
                </p>
                <div className="text-sm text-gray-400 mt-1">
                  <p>
                    Total Purchase Order Actual Cost: $
                    {formatNumber(
                      financeData.purchaseOrders.reduce(
                        (sum, po) => sum + (po.actualCost || 0),
                        0,
                      ),
                    )}
                  </p>
                  <p>
                    Total Work Order Actual Subtotal: $
                    {formatNumber(financeData.totalWorkOrderActualSubtotal)}
                  </p>
                  <p>
                    Total WSIB Cost: ${formatNumber(financeData.totalWsibCost)}
                  </p>
                  <p>
                    Total Pieceworker Burden Cost: $
                    {formatNumber(financeData.totalPieceworkerBurdenCost)}
                  </p>
                  <p>
                    Total Hourly Burden Cost: $
                    {formatNumber(financeData.totalBurdenCost)}
                  </p>
                  <p>
                    Total Forecasted Cost: $
                    {formatNumber(
                      financeData.purchaseOrders.reduce(
                        (sum, po) =>
                          sum +
                          (po.actualCost || 0) +
                          (po.pendingMOCost || 0) +
                          (po.pendingBillCost || 0),
                        0,
                      ) +
                        financeData.workOrders.reduce(
                          (sum, wo) =>
                            sum + (wo.actualCost || 0) + (wo.pendingCost || 0),
                          0,
                        ),
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-700 p-4 rounded-lg shadow mb-6">
            <h3 className="text-md font-semibold text-gray-200 mb-2">
              Purchase Orders
            </h3>
            <table className="min-w-full divide-y divide-gray-600">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Title
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    PO Number
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Estimated ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Actual ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Pending Material Orders ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Remaining ($)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {financeData.purchaseOrders.map((po, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {po.title}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {po.poNumber}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(po.estimatedCost)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(po.actualCost)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(po.pendingMOCost)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(po.remainingCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-700 p-4 rounded-lg shadow">
            <h3 className="text-md font-semibold text-gray-200 mb-2">
              Work Orders
            </h3>
            <table className="min-w-full divide-y divide-gray-600">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Title
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    WO Number
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Estimated ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Actual Subtotal ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Actual WSIB ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Actual Hourly Burden ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Actual Pieceworker Burden ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Actual Total ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Pending Total ($)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                    Remaining ($)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {financeData.workOrders.map((wo, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {wo.title}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {wo.woNumber}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(wo.estimatedCost)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(wo.actualCostBreakdown.subtotal)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(wo.actualCostBreakdown.wsibCost)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(wo.actualCostBreakdown.burdenCost)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(
                        wo.actualCostBreakdown.pieceworkerBurdenCost,
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(wo.actualCost)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(wo.pendingCost)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {formatNumber(wo.remainingCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsTab;
